import { ListItemButton, ListItemIcon, Box, ListItemText } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { Business_Profile_Enum } from '@kheops/graphql';
import { RoutePaths } from '../routes/AppRoutes';
import useCurrentTabs from '../topMenu/hooks/useCurrentTab';
import {
  currentContextAtom,
  globalAuthorizationAtom,
  userAtom,
} from '../state';
import {
  BuyerCompanyNavMap,
  NavigationCategories,
  SupplierCompanyNavMap,
} from '../common/state/navigation';

export default function UserCompanyNavigation(): React.JSX.Element {
  const { t } = useTranslation(['common', 'price', 'contracts', 'catalogs', 'settings']);
  const { realm } = useAtomValue(currentContextAtom);
  const { isStaff } = useAtomValue(userAtom);
  const navigate = useNavigate();
  const globalAuthorization = useAtomValue(globalAuthorizationAtom);
  const [currentCategory, setCurrentCategory] = useState<NavigationCategories>();
  const currentTabValue = useCurrentTabs([
    RoutePaths.COMPANY_PROFILE,
    RoutePaths.COMPANY_PRODUCTS,
    RoutePaths.COMPANY_DELIVERY_ZONES,
    RoutePaths.COMPANY_CUSTOM_PRICE_LISTS,
    RoutePaths.COMPANY_PRODUCTS_PRICES,
    RoutePaths.COMPANY_GLOBAL_DISCOUNTS,
    RoutePaths.COMPANY_BUYER_BILLING_INFO,
    RoutePaths.COMPANY_BUYER_DELIVERY_INFO,
    RoutePaths.COMPANY_CATALOGS,
    RoutePaths.COMPANY_TEAM,
    RoutePaths.COMPANY_SERVICES,
  ]);
  const userCompanyNavMap = realm === Business_Profile_Enum.Supplier
    ? SupplierCompanyNavMap
    : BuyerCompanyNavMap;

  useEffect(() => {
    const currentNavData = userCompanyNavMap.find((navData) => {
      if (navData.subTabs.length) {
        return !!navData.subTabs.find(
          (subTab) => subTab.to === currentTabValue,
        );
      }
      return navData.to === currentTabValue;
    });

    if (currentNavData) {
      setCurrentCategory(currentNavData.category);
    }
  }, [currentTabValue]);

  const mainTabs = useMemo(() => {
    return userCompanyNavMap
      .filter((item) => {
        const isAuthorized = !item.authorization
          || globalAuthorization.some(
            ({ object, permission }) =>
              item.authorization!.object === object
              && item.authorization!.permission === permission,
          );

        return isAuthorized || isStaff;
      })
      .flatMap((item) => {
        const isSelected = item.to === currentTabValue
          && !item.subTabs.some((subTab) => subTab.to === currentTabValue);

        const mainTab = (
          <ListItemButton
            key={item.to}
            onClick={() => navigate(item.to)}
            selected={isSelected}
          >
            <ListItemIcon>
              <item.icon width={24} height={24} />
            </ListItemIcon>
            <ListItemText>{t(item.label)}</ListItemText>
          </ListItemButton>
        );

        const nestedSubTabs = item.subTabs.length && currentCategory === item.category
          ? item.subTabs.map((subTab) => (
            <Box
              key={subTab.label}
              sx={{
                borderLeft: '1px solid',
                borderColor: 'divider',
              }}
            >
              <ListItemButton
                key={subTab.to}
                onClick={() => navigate(subTab.to)}
                selected={subTab.to === currentTabValue}
                sx={{
                  mx: 1,
                }}
              >
                <ListItemText>
                  {t(subTab.label)}
                </ListItemText>
              </ListItemButton>
            </Box>
          ))
          : [];

        return [mainTab, ...nestedSubTabs];
      });
  }, [currentTabValue, currentCategory]);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return (<>{mainTabs}</>);
}
